import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SERVICE_LIST = [
  { key: "smr", label: "Structured Medication Reviews (SMRs)", desc: "Patient-centred, pharmacist-led medication reviews for patients with complex polypharmacy, frailty, high-risk prescribing and other clinical needs." },
  { key: "polypharmacy", label: "Polypharmacy & Deprescribing", desc: "Medication reviews to optimise treatment, reduce inappropriate prescribing and improve patient safety." },
  { key: "opioid", label: "Opioid Reduction Programmes", desc: "Targeted patient reviews, prescribing optimisation and clinically appropriate opioid reduction plans." },
  { key: "gabapentinoid", label: "Gabapentinoid Prescribing Reviews", desc: "Review of gabapentin and pregabalin prescribing, monitoring, safety and clinical appropriateness." },
  { key: "high_risk", label: "High-Risk Medication Reviews", desc: "Clinical review and monitoring of patients receiving high-risk medicines." },
  { key: "valproate", label: "Valproate & Medicines Safety", desc: "Targeted medication safety reviews and support with relevant pregnancy prevention and risk minimisation requirements." },
  { key: "care_home", label: "Care Home Medication Reviews", desc: "Structured clinical pharmacy support and medicines optimisation for care home residents." },
  { key: "meds_rec", label: "Medicines Reconciliation", desc: "Medication reconciliation following hospital discharge and transitions of care." },
  { key: "prescribing_audit", label: "Prescribing Audits", desc: "Clinical audits, formulary adherence, prescribing quality improvement and targeted prescribing reviews." },
  { key: "ltc", label: "Long-Term Condition Medicines Management", desc: "Pharmacist-led medicines optimisation and prescribing support within agreed clinical competencies." },
  { key: "bespoke", label: "Bespoke PCN & ICB Projects", desc: "Describe your own clinical service requirements and we will scope a tailored project with you." },
];

export const ROLES = [
  { key: "pharmacist", label: "Clinical Pharmacist", group: "pharmacist" },
  { key: "senior_pharmacist", label: "Senior Clinical Pharmacist", group: "pharmacist" },
  { key: "independent_prescriber", label: "Independent Prescribing Pharmacist", group: "pharmacist" },
  { key: "technician", label: "Pharmacy Technician", group: "technician" },
];

export const DEFAULT_CONFIG = {
  rates: { pharmacist: 65, senior_pharmacist: 75, independent_prescriber: 85, technician: 38 },
  project_fee: 750,
  uncontactable_pct: 10,
  complex_case_pct: 10,
  pharmacy_sourcing_fee_public: "",
  service_times: {
    smr: { pharmacist_min: 45, technician_min: 15 },
    polypharmacy: { pharmacist_min: 40, technician_min: 15 },
    opioid: { pharmacist_min: 45, technician_min: 15 },
    gabapentinoid: { pharmacist_min: 40, technician_min: 15 },
    high_risk: { pharmacist_min: 40, technician_min: 20 },
    valproate: { pharmacist_min: 35, technician_min: 15 },
    care_home: { pharmacist_min: 40, technician_min: 20 },
    meds_rec: { pharmacist_min: 30, technician_min: 15 },
    prescribing_audit: { pharmacist_min: 35, technician_min: 20 },
    ltc: { pharmacist_min: 40, technician_min: 15 },
    bespoke: { pharmacist_min: 40, technician_min: 15 },
  },
};

export const WTE_HOURS = 37.5;

export const usePublicConfig = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  useEffect(() => {
    axios
      .get(`${API}/config/public`)
      .then((r) => setConfig({ ...DEFAULT_CONFIG, ...r.data }))
      .catch(() => {});
  }, []);
  return config;
};

export const gbp = (n) => "£" + Math.round(n).toLocaleString("en-GB");
export const fmt = (n, d = 1) =>
  Number(n.toFixed(d)).toLocaleString("en-GB", { maximumFractionDigits: d });

export function calcService(cfg, { serviceKey, patients, weeks, teamPref }) {
  const t = cfg.service_times[serviceKey] || { pharmacist_min: 40, technician_min: 15 };
  const overhead = 1 + (cfg.uncontactable_pct + cfg.complex_case_pct) / 100;
  const pharmacistHours = (patients * t.pharmacist_min * overhead) / 60;
  const technicianHours = teamPref === "team" ? (patients * t.technician_min * overhead) / 60 : 0;
  const weeklyHours = weeks > 0 ? (pharmacistHours + technicianHours) / weeks : 0;
  const pharmacistWTE = weeks > 0 ? pharmacistHours / (WTE_HOURS * weeks) : 0;
  const technicianWTE = weeks > 0 ? technicianHours / (WTE_HOURS * weeks) : 0;
  const cost =
    pharmacistHours * cfg.rates.pharmacist + technicianHours * cfg.rates.technician + cfg.project_fee;
  const perPatient = patients > 0 ? cost / patients : 0;
  return { pharmacistHours, technicianHours, weeklyHours, pharmacistWTE, technicianWTE, cost, perPatient, weeks };
}

export function calcWorkforce(cfg, roles, durationWeeks) {
  let weeklyPharmHours = 0;
  let weeklyTechHours = 0;
  let weeklyValue = 0;
  for (const r of ROLES) {
    const v = roles[r.key] || { count: 0, hours: 0 };
    const h = (Number(v.count) || 0) * (Number(v.hours) || 0);
    if (r.group === "pharmacist") weeklyPharmHours += h;
    else weeklyTechHours += h;
    weeklyValue += h * (cfg.rates[r.key] || 0);
  }
  return {
    weeklyPharmHours,
    weeklyTechHours,
    totalHours: (weeklyPharmHours + weeklyTechHours) * durationWeeks,
    pharmWTE: weeklyPharmHours / WTE_HOURS,
    techWTE: weeklyTechHours / WTE_HOURS,
    weeklyValue,
    monthlyValue: (weeklyValue * 52) / 12,
    totalValue: weeklyValue * durationWeeks,
  };
}
